import type { LanguageTTSConfig } from "./client";
import { AUDIO_LANGUGAGE_TO_CARTESIA_LANGUAGE } from "./providers/cartesia/lang";
import {
  CARTESIA_LANGUAGE_TO_VOICES,
  VOICE_ID_TO_CARTESIA_VOICE,
  type CartesiaVoice,
} from "./providers/cartesia/voices";
import { ELEVENLABS_DIALOGUE_MODEL_ID } from "./providers/elevenlabs/dialogueClient";
import {
  AUDIO_LANGUAGE_TO_ELEVEN_LABS_ACCENT,
  AUDIO_LANGUAGE_TO_ELEVEN_LABS_LANGUAGE,
} from "./providers/elevenlabs/lang";
import {
  elevenLabsLanguageAndAccentToVoices,
  elevenLabsVoiceKey,
  ElevenLabsVoice,
  VOICE_ID_TO_ELEVEN_LABS_VOICE,
} from "./providers/elevenlabs/voices";
import { getRegionalVoiceIds } from "./regionalVoices";
import type { LanguageToTTSConfig } from "@/backend/db/drizzle/types/jsonTypes";
import {
  hasLocalStorage,
  readLocalStorage,
  LocalStorageKey,
} from "@/lib/local-storage";
import { isDeploymentAllowed } from "@/lib/vendor-policy/policy";
import { listDeploymentsForVendorServiceModel } from "@/lib/vendor-policy/registry";
import type { CompliancePolicy } from "@/lib/vendor-policy/types";
import { notNull } from "@/utils/helpers";
import type { LocalizationRegion } from "@/utils/localizationRegion";
import type { AudioLanguage, SurveyConfig } from "@/utils/types";

export const voiceToTTSConfig = ({
  voice,
}: {
  voice: ElevenLabsVoice | CartesiaVoice;
}): LanguageTTSConfig => {
  return voice.provider === "cartesia"
    ? {
        provider: voice.provider,
        voiceId: voice.id,
        modelId: voice.modelId,
        language: voice.language,
      }
    : {
        provider: voice.provider,
        voiceId: voice.id,
        modelId: voice.modelId,
        language: voice.language,
      };
};

const ELEVENLABS_FLASH_MODEL_ID = "eleven_flash_v2_5";

const isElevenLabsFlash = (config: LanguageTTSConfig) =>
  config.provider === "elevenlabs" &&
  config.modelId === ELEVENLABS_FLASH_MODEL_ID;

// The catalog no longer offers Flash, so saves move stored Flash voices to v4 Turbo.
export const withV4TurboForFlashVoices = (
  languageToConfig: Partial<LanguageToTTSConfig>
): Partial<LanguageToTTSConfig> =>
  Object.fromEntries(
    Object.entries(languageToConfig).map(([language, config]) => [
      language,
      notNull(config) && isElevenLabsFlash(config)
        ? { ...config, modelId: ELEVENLABS_DIALOGUE_MODEL_ID }
        : config,
    ])
  );

export type TTSVoiceProvider = (ElevenLabsVoice | CartesiaVoice)["provider"];

export interface TtsModel {
  provider: TTSVoiceProvider;
  modelId: string;
}

export const ALL_TTS_VOICE_MODELS: readonly TtsModel[] = [
  ...new Map(
    [
      ...Object.values(VOICE_ID_TO_ELEVEN_LABS_VOICE),
      ...Object.values(VOICE_ID_TO_CARTESIA_VOICE),
    ].map((voice): [string, TtsModel] => [
      `${voice.provider}/${voice.modelId}`,
      { provider: voice.provider, modelId: voice.modelId },
    ])
  ).values(),
];

const isTtsModelAllowed = ({
  provider,
  modelId,
  policy,
}: {
  provider: TTSVoiceProvider;
  modelId: string;
  policy: CompliancePolicy;
}): boolean =>
  listDeploymentsForVendorServiceModel({
    vendor: provider,
    service: "tts",
    model: modelId,
  }).some((deployment) => isDeploymentAllowed(deployment, policy).isAllowed);

export const listAllowedTtsModels = (
  policy: CompliancePolicy
): readonly TtsModel[] =>
  ALL_TTS_VOICE_MODELS.filter(({ provider, modelId }) =>
    isTtsModelAllowed({ provider, modelId, policy })
  );

const getAllowedVoicesForAudioLanguage = ({
  language,
  allowedModels,
}: {
  language: AudioLanguage;
  allowedModels: readonly TtsModel[];
}) => {
  const voices = [];
  const elevenLabsLanguage = AUDIO_LANGUAGE_TO_ELEVEN_LABS_LANGUAGE[language];
  const elevenLabsAccent = AUDIO_LANGUAGE_TO_ELEVEN_LABS_ACCENT[language];
  if (elevenLabsLanguage) {
    const elevenLabsVoices = elevenLabsLanguageAndAccentToVoices({
      language: elevenLabsLanguage,
      accent: elevenLabsAccent,
    });
    voices.push(...elevenLabsVoices);
  }

  const cartesiaLanguage = AUDIO_LANGUGAGE_TO_CARTESIA_LANGUAGE[language];
  if (cartesiaLanguage) {
    const cartesiaVoices = CARTESIA_LANGUAGE_TO_VOICES[cartesiaLanguage] ?? [];
    voices.push(...cartesiaVoices);
  }
  return voices.filter((voice) =>
    allowedModels.some(
      (model) =>
        model.provider === voice.provider && model.modelId === voice.modelId
    )
  );
};

export const getAvailableVoicesForAudioLanguage = ({
  language,
  allowedModels = [],
  region = null,
}: {
  language: AudioLanguage;
  allowedModels?: readonly TtsModel[];
  region?: LocalizationRegion | null;
}) => {
  const allowedVoices = getAllowedVoicesForAudioLanguage({
    language,
    allowedModels,
  });
  const regionalVoiceIds = getRegionalVoiceIds({ language, region });
  const regionalVoices = regionalVoiceIds
    .map((voiceId) => allowedVoices.find((voice) => voice.id === voiceId))
    .filter(notNull);
  return [
    ...regionalVoices,
    ...allowedVoices.filter((voice) => !regionalVoiceIds.includes(voice.id)),
  ];
};

export const getDefaultVoiceForAudioLanguage = ({
  language,
  allowedModels,
  region = null,
}: {
  language: AudioLanguage;
  allowedModels?: readonly TtsModel[];
  region?: LocalizationRegion | null;
}) => {
  const availableVoices = getAvailableVoicesForAudioLanguage({
    language,
    allowedModels,
    region,
  });
  const regionalVoiceIds = getRegionalVoiceIds({ language, region });
  // Regional voices come first in availableVoices, so this finds the region's top pick.
  const regionalVoice = availableVoices.find((voice) =>
    regionalVoiceIds.includes(voice.id)
  );
  const feminineVoice = availableVoices.find(
    (voice) => voice.gender === "feminine"
  );
  return regionalVoice ?? feminineVoice ?? availableVoices[0] ?? null;
};

export const getTTSConfig = ({
  config,
  language,
}: {
  config: Pick<SurveyConfig, "ttsConfig">;
  language: AudioLanguage;
}) => {
  if (!config.ttsConfig.isEnabled) {
    return null;
  }

  return config.ttsConfig.languageToConfig[language] ?? null;
};

// The participant's stored mute preference wins; the study default covers
// first visits. v2 skips this and always uses the study default.
export const getInitialIsMuted = ({
  config,
}: {
  config: Pick<SurveyConfig, "ttsConfig">;
}): boolean =>
  hasLocalStorage(LocalStorageKey.HasMutedTTS)
    ? readLocalStorage(
        LocalStorageKey.HasMutedTTS,
        config.ttsConfig.defaultIsMuted
      )
    : config.ttsConfig.defaultIsMuted;

export const getVoice = ({
  voiceId,
  language,
}: {
  voiceId: string;
  language: AudioLanguage;
}): ElevenLabsVoice | CartesiaVoice | null => {
  const elevenLabsLanguage = AUDIO_LANGUAGE_TO_ELEVEN_LABS_LANGUAGE[language];
  return (
    (elevenLabsLanguage
      ? VOICE_ID_TO_ELEVEN_LABS_VOICE[
          elevenLabsVoiceKey(voiceId, elevenLabsLanguage)
        ]
      : null) ??
    VOICE_ID_TO_CARTESIA_VOICE[voiceId] ??
    null
  );
};
